import React, { useContext, useEffect, useMemo } from 'react';
import { AuthButtonGroup, Separator } from '@design-system';
import { AuthPageContext } from '../context';

interface AuthSocialLoginProps {
  /**
   * If `true`, the buttons will have a disable state.
   */
  isDisabled?: boolean;
  /**
   * On click handler that will trigger the auth0 authentication.
   * @param {string} conn - authentication connection method.
   */
  onClick: (conn: 'google-oauth2' | 'windowslive') => void;
  /**
   * Optional label for the separator (e.g., "o")
   */
  separatorLabel?: string;
}

/**
 * A teleporting component that renders the social login buttons 
 * into the fixed socialLogin slot of the AuthPages layout.
 */
export const AuthSocialLogin: React.FC<AuthSocialLoginProps> = ({
  isDisabled,
  onClick,
  separatorLabel,
}) => {
  const authPageContext = useContext(AuthPageContext);

  const content = useMemo(() => (
    <div className="w-full flex flex-col items-center">
      <Separator label={separatorLabel} />
      <AuthButtonGroup
        className="mt-6"
        isDisabled={isDisabled}
        onClick={onClick}
      />
    </div>
  ), [isDisabled, onClick, separatorLabel]);

  // If we are inside the AuthPageContext, we "teleport" the content to the social slot
  useEffect(() => {
    if (authPageContext) {
      authPageContext.setSocialLogin(content);
      // Cleanup on unmount
      return () => authPageContext.setSocialLogin(null);
    }
  }, [content, authPageContext]);

  // If we have the context, we return null here because the content is rendered in the slot
  if (authPageContext) {
    return null;
  }

  return content;
};
